import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import SearchableMultiSelectListCst from "@components/SearchableMultiSelectListCst";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { ServicesURL } from "@utils/utilsApi";
import LtiAccountRow from "./components/LtiAccountRow";
import {
  LtiAccountListItem,
  LtiAccountPickerProps,
  LtiAccountSelection,
} from "./types";

const PAGE_SIZE = 10;

const toSelection = (item: LtiAccountListItem): LtiAccountSelection => ({
  id: item.idCorporateAccount,
  label: item.accountName,
});

const LtiAccountPickerCst: React.FC<LtiAccountPickerProps> = ({
  open,
  onClose,
  title,
  initialSelection,
  onConfirm,
}) => {
  const { t } = useTranslation("lti-account-picker");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchSubmitted, setSearchSubmitted] = useState("");
  const [selected, setSelected] = useState<LtiAccountSelection | null>(null);

  const url = `${ServicesURL.getLTIAccounts}?requestMethod=GET${
    corporateId && corporateId !== "1" ? `&corporateId=${corporateId}` : ""
  }`;

  const { items, totalItems, totalPages, isLoading } =
    usePaginatedList<LtiAccountListItem>(url, {
      page,
      pageSize,
      search: searchSubmitted || undefined,
    });

  useEffect(() => {
    if (!open) return;
    setSelected(initialSelection);
    setPage(1);
    setSearchSubmitted("");
  }, [open, initialSelection]);

  const selectedSet = useMemo(
    () => new Set<string | number>(selected ? [selected.id] : []),
    [selected],
  );

  const handleToggle = (id: string | number, next: boolean) => {
    if (!next) {
      setSelected(null);
      return;
    }
    const numericId = typeof id === "string" ? Number(id) : id;
    const found = items.find((it) => it.idCorporateAccount === numericId);
    if (found) setSelected(toSelection(found));
  };

  const handleSearch = (value: string) => {
    setSearchSubmitted(value);
    setPage(1);
  };

  const handleConfirm = () => {
    onConfirm(selected);
    onClose();
  };

  const canConfirm = !!selected;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px",
          color: theme.palette.text.primary,
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ color: theme.palette.text.primary }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "12px 24px 16px" }}>
        <SearchableMultiSelectListCst<LtiAccountListItem>
          selectionMode="single"
          showSelectedCount={false}
          items={items}
          getId={(it) => it.idCorporateAccount}
          renderRow={({ item }) => <LtiAccountRow item={item} />}
          selectedIds={selectedSet}
          onToggle={handleToggle}
          initialSearchValue={searchSubmitted}
          onSearchSubmit={handleSearch}
          searchPlaceholder={t("search-placeholder") || ""}
          searchButtonLabel={t("search-button") || ""}
          page={page}
          pageSize={pageSize}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          isLoading={isLoading}
          selectedCountLabel=""
          emptyLabel={t("empty") || ""}
        />
      </DialogContent>

      <DialogActions sx={{ padding: "16px 24px 20px", gap: 2 }}>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!canConfirm}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": { backgroundColor: theme.palette.primary.dark },
          }}
        >
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LtiAccountPickerCst;
